import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { PageSEO } from "@/components/PageSEO";
import { PageHero } from "@/components/PageHero";
import { useLang } from "@/context/LanguageContext";
import {
  User, Building2, Phone, Mail, Ruler, Factory,
  CreditCard, MapPin, MessageSquare, Send, CheckCircle, ArrowRight, ShieldCheck,
  IndianRupee, Loader2
} from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BOOKING_HERO = "https://static.prod-images.emergentagent.com/jobs/887722b5-7ad9-4cfc-8366-fffd7eed17f9/images/a31d2b3f6b1cb7255440086e3b828476059e068d6fff380dcfe63960cbcf7f93.jpeg";

const RATE = 7500;

const INDUSTRY_TYPES = [
  "Manufacturing Unit",
  "Warehouse / Storage",
  "Food Processing",
  "Pharmaceutical",
  "Engineering / Fabrication",
  "Auto Components",
  "Textile / Garments",
  "Chemical Industry",
  "Electronics Assembly",
  "Cold Storage",
  "Other",
];

const PAYMENT_MODES = [
  "Full Cash Payment",
  "Bank Term Loan",
  "EMI / Installments",
  "NBFC Finance",
  "Government Subsidy + Loan",
];

const bookingSteps = [
  { step: "01", title: "Fill Booking Form", desc: "Submit your details and preferred plot size." },
  { step: "02", title: "Team Contacts You", desc: "Our team calls within 24 hours to discuss your requirement." },
  { step: "03", title: "Site Visit", desc: "We arrange a guided site visit to IIE Sigaddi." },
  { step: "04", title: "Pay 10% Booking", desc: "Pay 10% of total plot cost to confirm booking." },
  { step: "05", title: "Allotment Letter", desc: "Pay 15% within 30 days to receive allotment letter." },
  { step: "06", title: "Lease Deed", desc: "Pay balance 75% at lease deed within 6 months." },
];

function formatINR(amount) {
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} L`;
  return `₹${amount.toLocaleString("en-IN")}`;
}

export default function Booking() {
  const { t } = useLang();
  const [sizeOptions, setSizeOptions] = useState([]);
  const priceMap = useMemo(
    () => Object.fromEntries(sizeOptions.map((o) => [o.label, o.price])),
    [sizeOptions]
  );
  const [form, setForm] = useState({
    full_name: "",
    company_name: "",
    phone: "",
    email: "",
    plot_size: "",
    industry_type: "",
    payment_mode: "",
    address: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [bookingId, setBookingId] = useState(null);
  const [paymentStep, setPaymentStep] = useState(false);
  const [paymentDone, setPaymentDone] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/payments/config`).then((res) => {
      setPaymentConfig(res.data);
    }).catch(() => {});
  }, []);

  // Extract real plot sizes from the Plots site-map data (flat ₹7,500/sq.mtr)
  useEffect(() => {
    axios.get(`${API}/site-plots`).then((res) => {
      const uniq = Array.from(new Set(res.data.map((p) => p.plot_size_sqmt))).sort((a, b) => a - b);
      setSizeOptions(uniq.map((size) => ({
        size,
        label: `${size.toLocaleString("en-IN")} Sq.Mtr (₹7,500/sqmtr)`,
        price: size * RATE,
      })));
    }).catch(() => {});
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSendOTP = async () => {
    if (!form.email || !form.email.includes("@")) {
      toast.error("Please enter a valid email first");
      return;
    }
    setOtpLoading(true);
    try {
      await axios.post(`${API}/otp/send`, { email: form.email });
      setOtpSent(true);
      toast.success("OTP sent to your email!");
    } catch {
      toast.error("Failed to send OTP. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otpCode || otpCode.length !== 6) {
      toast.error("Please enter 6-digit OTP");
      return;
    }
    setOtpLoading(true);
    try {
      await axios.post(`${API}/otp/verify`, { email: form.email, otp: otpCode });
      setOtpVerified(true);
      toast.success("Email verified successfully!");
    } catch {
      toast.error("Invalid or expired OTP");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.email || !form.plot_size || !form.industry_type || !form.payment_mode || !form.address) {
      toast.error("Please fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/bookings`, form);
      const newBookingId = res.data.id;
      setBookingId(newBookingId);

      // Check if Razorpay is enabled and plot has known price
      const totalPrice = priceMap[form.plot_size];
      if (paymentConfig?.enabled && totalPrice) {
        setPaymentStep(true);
        toast.success("Booking registered! You can now pay 10% EMD online.");
      } else {
        setSubmitted(true);
        toast.success("Booking request submitted! We'll contact you within 24 hours.");
      }
    } catch {
      toast.error("Submission failed. Please call +91-9258959058");
    } finally {
      setSubmitting(false);
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) { resolve(true); return; }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayEMD = async () => {
    const totalPrice = priceMap[form.plot_size];
    if (!totalPrice || !bookingId) return;

    setPaymentLoading(true);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        toast.error("Payment gateway failed to load. Please try again.");
        setPaymentLoading(false);
        return;
      }

      const emdAmount = Math.round(totalPrice * 0.1);
      const orderRes = await axios.post(`${API}/payments/create-order`, {
        booking_id: bookingId,
        amount: emdAmount * 100, // paise
        plot_size: form.plot_size,
        customer_name: form.full_name,
        customer_email: form.email,
        customer_phone: form.phone,
      });

      const { order_id, amount, key_id } = orderRes.data;

      const options = {
        key: key_id,
        amount: amount,
        currency: "INR",
        name: "VS Agro Infra Enterprises",
        description: `10% EMD - ${form.plot_size}`,
        order_id: order_id,
        handler: async (response) => {
          try {
            await axios.post(`${API}/payments/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setPaymentDone(true);
            setSubmitted(true);
            setPaymentStep(false);
            toast.success("Payment successful! Your booking is confirmed.");
          } catch {
            toast.error("Payment verification failed. Please contact support.");
          }
        },
        prefill: {
          name: form.full_name,
          email: form.email,
          contact: form.phone,
        },
        theme: {
          color: "#F97316",
        },
        modal: {
          ondismiss: () => {
            setPaymentLoading(false);
            toast.info("Payment cancelled. You can pay later or contact our team.");
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
      setPaymentLoading(false);
    } catch (err) {
      toast.error("Failed to initiate payment. Please try again.");
      setPaymentLoading(false);
    }
  };

  const emdAmount = priceMap[form.plot_size] ? Math.round(priceMap[form.plot_size] * 0.1) : 0;
  const totalPrice = priceMap[form.plot_size] || 0;

  return (
    <div className="bg-[#0F172A]">
      <PageSEO page="booking" />
      <Navbar />

      {/* Header */}
      <PageHero
        image={BOOKING_HERO}
        tag="Reserve Your Plot"
        title={t("booking.title")}
        subtitle={t("booking.subtitle")}
        breadcrumb={{ home: t("nav.home"), current: t("booking.breadcrumb") }}
      />

      {/* Booking Process Steps */}
      <div className="bg-orange-500 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {bookingSteps.map((s) => (
              <div key={s.step} className="text-center">
                <div className="text-white font-black text-xl mb-1"
                  style={{ fontFamily: "'JetBrains Mono', monospace" }}>{s.step}</div>
                <div className="text-white font-bold text-xs uppercase tracking-wide">{s.title}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-[#0F172A] py-12" data-testid="booking-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* Info Panel */}
            <div className="space-y-5">
              <div className="bg-[#1E293B] border border-white/10 text-white p-6">
                <h3 className="font-bold text-xl mb-4 uppercase"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  {t("booking.plotPricing")}
                </h3>
                <p className="text-slate-400 text-xs mb-3">Actual plot sizes available at IIE Sigaddi — flat ₹7,500/sq.mtr</p>
                <div className="max-h-72 overflow-y-auto pr-1" data-testid="booking-price-table">
                  <table className="w-full text-sm">
                    <tbody>
                      {sizeOptions.length === 0 ? (
                        <tr><td className="py-2 text-slate-500 text-xs">Loading plot sizes…</td></tr>
                      ) : sizeOptions.map((o) => (
                        <tr key={o.size} className="border-b border-slate-800 last:border-0">
                          <td className="py-2 text-slate-300 text-xs">{o.size.toLocaleString("en-IN")} sqmtr</td>
                          <td className="py-2 text-orange-400 text-xs text-center"
                            style={{ fontFamily: "'JetBrains Mono', monospace" }}>₹7,500/sq</td>
                          <td className="py-2 text-slate-400 text-xs text-right">{formatINR(o.size * RATE)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-[#1E293B] border border-white/10 p-5 space-y-3">
                <h4 className="font-bold text-white text-sm uppercase tracking-wide">{t("booking.whyBookWithUs")}</h4>
                {[
                  "SIDA site map approval — 100% legal",
                  "SIIDCUL adjacent — prime location",
                  "Flexible payment terms",
                  "Bank finance assistance",
                  "Govt incentives eligible",
                  "Spot allotment available",
                ].map((f) => (
                  <div key={f} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300 text-sm">{f}</span>
                  </div>
                ))}
              </div>

              <div className="bg-orange-500/10 border border-orange-500/30 p-5">
                <h4 className="font-bold text-orange-400 text-sm uppercase mb-3">{t("booking.paymentPolicyTitle")}</h4>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-orange-500 text-white flex items-center justify-center font-black text-sm flex-shrink-0"
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}>10%</div>
                    <div>
                      <div className="text-white text-sm font-bold">{t("booking.policy10Title")}</div>
                      <div className="text-slate-400 text-xs">{t("booking.policy10Desc")}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-orange-500 text-white flex items-center justify-center font-black text-sm flex-shrink-0"
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}>15%</div>
                    <div>
                      <div className="text-white text-sm font-bold">{t("booking.policy15Title")}</div>
                      <div className="text-slate-400 text-xs">{t("booking.policy15Desc")}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-orange-500 text-white flex items-center justify-center font-black text-sm flex-shrink-0"
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}>75%</div>
                    <div>
                      <div className="text-white text-sm font-bold">{t("booking.policy75Title")}</div>
                      <div className="text-slate-400 text-xs">{t("booking.policy75Desc")}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* EMD Calculator - shown when plot is selected */}
              {form.plot_size && totalPrice > 0 && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 p-5" data-testid="emd-calculator">
                  <h4 className="font-bold text-emerald-400 text-sm uppercase mb-3 flex items-center gap-2">
                    <IndianRupee className="w-4 h-4" /> EMD Calculator
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Plot Price</span>
                      <span className="text-white font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{formatINR(totalPrice)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">10% EMD</span>
                      <span className="text-emerald-400 font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{formatINR(emdAmount)}</span>
                    </div>
                    {paymentConfig?.enabled && (
                      <div className="pt-2 border-t border-emerald-500/20">
                        <span className="text-emerald-300 text-xs flex items-center gap-1">
                          <CreditCard className="w-3 h-3" /> Online payment available via Razorpay
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Booking Form / Payment Step / Success */}
            <div className="lg:col-span-2">
              {submitted ? (
                <div className="bg-[#1E293B] border border-white/10 p-10 text-center" data-testid="booking-success">
                  <div className="w-16 h-16 bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-emerald-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: "uppercase" }}>
                    {paymentDone ? "Booking Confirmed!" : t("booking.successTitle")}
                  </h3>
                  <p className="text-slate-400 mb-2 text-sm">
                    {paymentDone
                      ? "Your 10% EMD payment has been received. Our team will contact you within 24 hours."
                      : t("booking.successMsg")}
                  </p>
                  {paymentDone && (
                    <div className="bg-emerald-500/10 border border-emerald-500/30 inline-flex items-center gap-2 px-4 py-2 mb-4">
                      <IndianRupee className="w-4 h-4 text-emerald-400" />
                      <span className="text-emerald-400 font-bold text-sm" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                        EMD Paid: {formatINR(emdAmount)}
                      </span>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-3 justify-center mt-4">
                    <a href="tel:+919258959058"
                      className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-2.5 font-bold uppercase tracking-wider text-sm transition-colors">
                      <Phone className="w-4 h-4" /> {t("booking.callNow")}
                    </a>
                    <Link to="/plots"
                      className="flex items-center gap-2 border border-white/20 text-white hover:border-orange-500 px-6 py-2.5 font-bold uppercase tracking-wider text-sm transition-colors">
                      {t("booking.viewPlots")} <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              ) : paymentStep ? (
                /* Payment Step */
                <div className="bg-[#1E293B] border border-white/10 p-6 md:p-8" data-testid="payment-step">
                  <h3 className="text-white font-bold text-2xl mb-1 uppercase"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                    Pay 10% Earnest Money Deposit
                  </h3>
                  <p className="text-slate-400 text-sm mb-6">
                    Secure your plot booking by paying 10% EMD online via Razorpay.
                  </p>

                  {/* Booking Summary */}
                  <div className="bg-slate-800/50 border border-white/5 p-4 mb-6 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Name</span>
                      <span className="text-white font-medium">{form.full_name}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Plot Size</span>
                      <span className="text-white font-medium">{form.plot_size}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Industry</span>
                      <span className="text-white font-medium">{form.industry_type}</span>
                    </div>
                    <div className="h-px bg-white/10 my-2" />
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Total Plot Price</span>
                      <span className="text-white font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{formatINR(totalPrice)}</span>
                    </div>
                    <div className="flex justify-between text-lg">
                      <span className="text-orange-400 font-bold">10% EMD Amount</span>
                      <span className="text-orange-400 font-black" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{formatINR(emdAmount)}</span>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={handlePayEMD}
                      disabled={paymentLoading}
                      className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-3.5 font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2 transition-all duration-300 hover:shadow-[0_0_20px_rgba(249,115,22,0.3)] disabled:opacity-60"
                      data-testid="pay-emd-btn"
                    >
                      {paymentLoading ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>
                      ) : (
                        <><CreditCard className="w-4 h-4" /> Pay {formatINR(emdAmount)} Now</>
                      )}
                    </button>
                    <button
                      onClick={() => { setSubmitted(true); setPaymentStep(false); }}
                      className="border border-white/20 text-white hover:border-orange-500 py-3.5 px-6 font-bold uppercase tracking-wider text-sm transition-colors"
                      data-testid="skip-payment-btn"
                    >
                      Pay Later
                    </button>
                  </div>

                  <p className="text-center text-xs text-slate-500 mt-4">
                    Secure payment powered by Razorpay. You can also pay later via bank transfer.
                  </p>
                </div>
              ) : (
                <div className="bg-[#1E293B] border border-white/10 p-6 md:p-8">
                  <h3 className="text-white font-bold text-2xl mb-1 uppercase"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                    {t("booking.formTitle")}
                  </h3>
                  <p className="text-slate-400 text-sm mb-6">{t("booking.formSubtitle")}</p>

                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="booking-form">
                    {/* Row 1 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">
                          <User className="inline w-3.5 h-3.5 mr-1" /> Full Name *
                        </label>
                        <input name="full_name" value={form.full_name} onChange={handleChange} required
                          placeholder="Your full name"
                          className="w-full border border-slate-600 px-3 py-2.5 text-sm focus:outline-none focus:border-orange-500 bg-slate-800 text-white placeholder-slate-500"
                          data-testid="booking-name-input" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">
                          <Building2 className="inline w-3.5 h-3.5 mr-1" /> Company / Firm Name
                        </label>
                        <input name="company_name" value={form.company_name} onChange={handleChange}
                          placeholder="Company or firm name (if any)"
                          className="w-full border border-slate-600 px-3 py-2.5 text-sm focus:outline-none focus:border-orange-500 bg-slate-800 text-white placeholder-slate-500"
                          data-testid="booking-company-input" />
                      </div>
                    </div>

                    {/* Row 2 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">
                          <Phone className="inline w-3.5 h-3.5 mr-1" /> Phone Number *
                        </label>
                        <input name="phone" value={form.phone} onChange={handleChange} required
                          placeholder="+91 XXXXX XXXXX"
                          className="w-full border border-slate-600 px-3 py-2.5 text-sm focus:outline-none focus:border-orange-500 bg-slate-800 text-white placeholder-slate-500"
                          data-testid="booking-phone-input" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">
                          <Mail className="inline w-3.5 h-3.5 mr-1" /> Email Address *
                        </label>
                        <div className="flex gap-2">
                          <input name="email" value={form.email} onChange={(e) => { handleChange(e); setOtpSent(false); setOtpVerified(false); setOtpCode(""); }} required type="email"
                            placeholder="your@email.com"
                            className="flex-1 border border-slate-600 px-3 py-2.5 text-sm focus:outline-none focus:border-orange-500 bg-slate-800 text-white placeholder-slate-500"
                            data-testid="booking-email-input" />
                          {!otpVerified && (
                            <button type="button" onClick={handleSendOTP} disabled={otpLoading || !form.email}
                              className="px-3 py-2.5 bg-orange-500/20 border border-orange-500/40 text-orange-400 text-xs font-bold uppercase tracking-wider hover:bg-orange-500/30 transition-colors disabled:opacity-50 whitespace-nowrap"
                              data-testid="booking-send-otp-btn">
                              {otpLoading ? "..." : otpSent ? "Resend" : "Verify"}
                            </button>
                          )}
                          {otpVerified && (
                            <div className="flex items-center gap-1 px-3 bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-xs font-bold">
                              <ShieldCheck className="w-4 h-4" /> Verified
                            </div>
                          )}
                        </div>
                        {otpSent && !otpVerified && (
                          <div className="flex gap-2 mt-2">
                            <input
                              type="text"
                              value={otpCode}
                              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                              placeholder="Enter 6-digit OTP"
                              maxLength={6}
                              className="flex-1 border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-slate-800 text-white placeholder-slate-500"
                              style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: "4px" }}
                              data-testid="booking-otp-input"
                            />
                            <button type="button" onClick={handleVerifyOTP} disabled={otpLoading || otpCode.length !== 6}
                              className="px-4 py-2 bg-emerald-500 text-white text-xs font-bold uppercase tracking-wider hover:bg-emerald-600 transition-colors disabled:opacity-50"
                              data-testid="booking-verify-otp-btn">
                              {otpLoading ? "..." : "Verify OTP"}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Row 3 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">
                          <Ruler className="inline w-3.5 h-3.5 mr-1" /> Preferred Plot Size *
                        </label>
                        <select name="plot_size" value={form.plot_size} onChange={handleChange} required
                          className="w-full border border-slate-600 px-3 py-2.5 text-sm focus:outline-none focus:border-orange-500 bg-slate-800 text-white"
                          data-testid="booking-size-select">
                          <option value="">Select plot size</option>
                          {sizeOptions.map((o) => <option key={o.size} value={o.label}>{o.label}</option>)}
                          <option value="Custom Size">Custom Size</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">
                          <Factory className="inline w-3.5 h-3.5 mr-1" /> Industry Type *
                        </label>
                        <select name="industry_type" value={form.industry_type} onChange={handleChange} required
                          className="w-full border border-slate-600 px-3 py-2.5 text-sm focus:outline-none focus:border-orange-500 bg-slate-800 text-white"
                          data-testid="booking-industry-select">
                          <option value="">Select industry type</option>
                          {INDUSTRY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                    </div>

                    {/* Row 4 */}
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-1">
                        <CreditCard className="inline w-3.5 h-3.5 mr-1" /> Preferred Payment Mode *
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {PAYMENT_MODES.map((mode) => (
                          <label key={mode}
                            className={`flex items-center gap-2 border px-3 py-2.5 cursor-pointer text-sm transition-colors ${
                              form.payment_mode === mode
                                ? "border-orange-500 bg-orange-500/10 text-orange-400"
                                : "border-slate-600 hover:border-orange-500/50 text-slate-400"
                            }`}>
                            <input type="radio" name="payment_mode" value={mode}
                              checked={form.payment_mode === mode}
                              onChange={handleChange}
                              className="text-orange-500"
                              data-testid={`payment-mode-${mode.replace(/\s+/g, "-").toLowerCase()}`} />
                            <span>{mode}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Row 5 */}
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-1">
                        <MapPin className="inline w-3.5 h-3.5 mr-1" /> Full Address *
                      </label>
                      <textarea name="address" value={form.address} onChange={handleChange} required rows={2}
                        placeholder="Your complete address (city, state)"
                        className="w-full border border-slate-600 px-3 py-2.5 text-sm focus:outline-none focus:border-orange-500 bg-slate-800 text-white placeholder-slate-500 resize-none"
                        data-testid="booking-address-input" />
                    </div>

                    {/* Row 6 */}
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-1">
                        <MessageSquare className="inline w-3.5 h-3.5 mr-1" /> Additional Requirements
                      </label>
                      <textarea name="message" value={form.message} onChange={handleChange} rows={3}
                        placeholder="Any specific requirements, questions, or notes..."
                        className="w-full border border-slate-600 px-3 py-2.5 text-sm focus:outline-none focus:border-orange-500 bg-slate-800 text-white placeholder-slate-500 resize-none"
                        data-testid="booking-message-input" />
                    </div>

                    <button type="submit" disabled={submitting || !otpVerified}
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3.5 font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2 transition-all duration-300 hover:shadow-[0_0_20px_rgba(249,115,22,0.3)] disabled:opacity-60"
                      data-testid="booking-submit-btn">
                      <Send className="w-4 h-4" />
                      {submitting ? t("booking.submitting") : !otpVerified ? "Verify Email First" : t("booking.submitBooking")}
                    </button>

                    <p className="text-center text-xs text-slate-500">
                      {t("booking.nonBinding")}
                    </p>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
